import { useMemo } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { startOfWeek, getDay, isWithinInterval } from 'date-fns';

const filtrarDadosDaSemana = (items, getDateFromItem) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { segunda: [], terca: [], quarta: [], quinta: [], sexta: [] };
  }

  const hoje = new Date();
  const inicioDaSemana = startOfWeek(hoje, { weekStartsOn: 1 });
  const intervaloDaSemana = { start: inicioDaSemana, end: hoje };

  const itensDaSemana = items.filter((item) => {
    const rawDate = getDateFromItem(item);
    if (!rawDate) return false;
    const dataAtendimento = new Date(rawDate);
    if (Number.isNaN(dataAtendimento.getTime())) return false;
    return isWithinInterval(dataAtendimento, intervaloDaSemana);
  });

  const diasDaSemana = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

  return itensDaSemana.reduce(
    (grupos, item) => {
      const dataAtendimento = new Date(getDateFromItem(item));
      if (Number.isNaN(dataAtendimento.getTime())) return grupos;
      const nomeDoDia = diasDaSemana[getDay(dataAtendimento)];
      if (grupos[nomeDoDia]) grupos[nomeDoDia].push(item);
      return grupos;
    },
    { segunda: [], terca: [], quarta: [], quinta: [], sexta: [] }
  );
};

const drawBadge = (ctx, x, y, text, bgColor) => {
  const label = String(text);
  const paddingX = 6;
  const height = 18;
  const radius = 9;
  const width = Math.max(18, ctx.measureText(label).width + paddingX * 2);
  const left = x - width / 2;
  const top = y - height / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left + radius, top);
  ctx.lineTo(left + width - radius, top);
  ctx.quadraticCurveTo(left + width, top, left + width, top + radius);
  ctx.lineTo(left + width, top + height - radius);
  ctx.quadraticCurveTo(left + width, top + height, left + width - radius, top + height);
  ctx.lineTo(left + radius, top + height);
  ctx.quadraticCurveTo(left, top + height, left, top + height - radius);
  ctx.lineTo(left, top + radius);
  ctx.quadraticCurveTo(left, top, left + radius, top);
  ctx.closePath();
  ctx.fillStyle = bgColor;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255,255,255,0.22)';
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x, y + 0.5);
  ctx.restore();
};

function WeeklyChart({ atendidos, naoAtendidos = [] }) {
  const atendidosSemana = useMemo(
    () => filtrarDadosDaSemana(atendidos, (item) => item?.dataFim),
    [atendidos]
  );

  const naoAtendidosSemana = useMemo(
    () =>
      filtrarDadosDaSemana(
        naoAtendidos,
        (item) => item?.timestamp || item?.dataSaida || item?.createdAt
      ),
    [naoAtendidos]
  );

  const diasLabel = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
  const atendidosDataPoints = diasLabel.map((dia) => atendidosSemana[dia]?.length || 0);
  const naoAtendidosDataPoints = diasLabel.map((dia) => naoAtendidosSemana[dia]?.length || 0);

  const lineChartData = {
    labels: ['Seg', 'Ter', 'Qua', 'Qui', 'Sexta'],
    datasets: [
      {
        label: 'Atendidos',
        data: atendidosDataPoints,
        borderColor: '#635bff',
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          if (!ctx) return 'rgba(99, 91, 255, 0.1)';
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height * 0.8);
          gradient.addColorStop(0, 'rgba(99, 91, 255, 0.5)');
          gradient.addColorStop(1, 'rgba(99, 91, 255, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4,
        pointBackgroundColor: '#635bff',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#635bff',
      },
      {
        label: 'Nao atendidos',
        data: naoAtendidosDataPoints,
        borderColor: '#ef4444',
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          if (!ctx) return 'rgba(239, 68, 68, 0.1)';
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height * 0.8);
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.5)');
          gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 4,
        pointStyle: 'circle',
        pointBackgroundColor: '#ef4444',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        pointHoverHitRadius: 8,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#ef4444',
      },
    ],
  };

  const valueLabelPlugin = {
    id: 'valueLabelPlugin',
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      ctx.save();
      ctx.font = "700 11px 'Poppins', sans-serif";

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta || !meta.data) return;

        const values = dataset.data || [];
        meta.data.forEach((point, index) => {
          const value = values[index] ?? 0;
          const isPrimeiraSerie = datasetIndex === 0;
          const yOffset = 14;
          const yRaw = point.y - yOffset;
          const yMinVisible = chart.chartArea.top + 14;
          const y = Math.max(yRaw, yMinVisible);
          const x = isPrimeiraSerie ? point.x - 8 : point.x + 8;
          const badgeColor = isPrimeiraSerie
            ? 'rgba(99, 91, 255, 0.95)'
            : 'rgba(239, 68, 68, 0.95)';
          drawBadge(ctx, x, y, value, badgeColor);
        });
      });

      ctx.restore();
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 18 },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#cbd5e1',
          font: { family: "'Poppins', sans-serif", size: 13, weight: 600 },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 10,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleColor: '#e2e8f0',
        bodyColor: '#9ca3af',
        borderColor: '#334155',
        borderWidth: 1.2,
        padding: 12,
        cornerRadius: 10,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#a8b3cf',
          font: { family: "'Poppins', sans-serif", size: 13, weight: 500 },
        },
      },
      y: {
        border: { display: false },
        grid: {
          color: 'rgba(148, 163, 184, 0.12)',
          drawBorder: false,
          borderDash: [4, 4],
        },
        ticks: {
          color: '#93a1c0',
          font: { family: "'Poppins', sans-serif", size: 12, weight: 500 },
          stepSize: 1,
        },
        suggestedMax: Math.max(...atendidosDataPoints, ...naoAtendidosDataPoints) + 2,
        beginAtZero: true,
      },
    },
  };

  return (
    <Card
      sx={{
        height: '100%',
        background:
          'radial-gradient(1200px 300px at -10% -20%, rgba(99,91,255,0.18), transparent 45%), radial-gradient(1000px 280px at 110% -10%, rgba(239,68,68,0.16), transparent 42%), #1e293b',
        color: 'white',
        boxShadow:
          '0 18px 40px -20px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
        border: '1px solid rgba(148, 163, 184, 0.16)',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Atendimentos da semana
        </Typography>
        <Box sx={{ height: 320, p: 1 }}>
          <Line data={lineChartData} options={lineChartOptions} plugins={[valueLabelPlugin]} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default WeeklyChart;

